import * as React from 'react';
import { ShortsVariant, ShortsSideTrianglesPosition } from 'types/kitLab';
import { outlineStrokeProps } from 'helpers/kitLab';

interface Props {
  primaryColor: string;
  secondaryColor: string;
  variant?: ShortsVariant;
  sideTrianglesPosition?: ShortsSideTrianglesPosition;
  className?: string;
}

export const KitShortsPreview: React.FC<Props> = ({
  primaryColor,
  secondaryColor,
  variant = 'plain',
  sideTrianglesPosition = 'BOTTOM',
  className
}) => {
  const svgClassName = ['eKitLabShortsPreview', className].filter(Boolean).join(' ');

  const waistY = 26;
  const bodyTopY = waistY + 3;
  const bodyBottomY = 70;
  const leftTopX = 36;
  const rightTopX = 84;
  const leftBottomX = 30;
  const rightBottomX = 90;
  const crotchTopY = 52;
  const crotchX = 60;

  const leftXAtY = (y: number) => {
    const t = (y - bodyTopY) / (bodyBottomY - bodyTopY);
    return leftTopX + (leftBottomX - leftTopX) * t;
  };

  const rightXAtY = (y: number) => {
    const t = (y - bodyTopY) / (bodyBottomY - bodyTopY);
    return rightTopX + (rightBottomX - rightTopX) * t;
  };

  const bodyPath = `
    M ${leftTopX} ${bodyTopY}
    L ${rightTopX} ${bodyTopY}
    L ${rightBottomX} ${bodyBottomY}
    Q ${rightBottomX - 2} ${bodyBottomY - 4} ${crotchX + 5} ${bodyBottomY}
    L ${crotchX} ${crotchTopY}
    L ${crotchX - 5} ${bodyBottomY}
    Q ${leftBottomX + 2} ${bodyBottomY - 4} ${leftBottomX} ${bodyBottomY}
    Z
  `;

  const clipIdRef = React.useRef<string>();
  if (!clipIdRef.current) {
    clipIdRef.current = `shortsClip-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
  }
  const clipId = clipIdRef.current;

  const renderSideStripes = () => {
    if (variant !== 'sideStripes') return null;

    const stripeTopY = bodyTopY + 1;
    const stripeBottomY = bodyBottomY - 1;

    const outerOffset = 1.5;
    const innerOffset = 5;

    const leftOuterTopX = leftXAtY(stripeTopY) + outerOffset;
    const leftInnerTopX = leftXAtY(stripeTopY) + innerOffset;
    const leftOuterBottomX = leftXAtY(stripeBottomY) + outerOffset;
    const leftInnerBottomX = leftXAtY(stripeBottomY) + innerOffset;

    const rightOuterTopX = rightXAtY(stripeTopY) - outerOffset;
    const rightInnerTopX = rightXAtY(stripeTopY) - innerOffset;
    const rightOuterBottomX = rightXAtY(stripeBottomY) - outerOffset;
    const rightInnerBottomX = rightXAtY(stripeBottomY) - innerOffset;

    return (
      <>
        <polygon
          points={`
            ${leftOuterTopX},${stripeTopY}
            ${leftInnerTopX},${stripeTopY}
            ${leftInnerBottomX},${stripeBottomY}
            ${leftOuterBottomX},${stripeBottomY}
          `}
          fill={secondaryColor}
        />
        <polygon
          points={`
            ${rightInnerTopX},${stripeTopY}
            ${rightOuterTopX},${stripeTopY}
            ${rightOuterBottomX},${stripeBottomY}
            ${rightInnerBottomX},${stripeBottomY}
          `}
          fill={secondaryColor}
        />
      </>
    );
  };

  const renderSideTriangles = () => {
    if (variant !== 'sideTriangles') return null;

    const topTriangles = sideTrianglesPosition === 'TOP' || sideTrianglesPosition === 'BOTH';
    const bottomTriangles = sideTrianglesPosition === 'BOTTOM' || sideTrianglesPosition === 'BOTH';

    const triHeight = 12;

    const topBaseY = bodyTopY;
    const bottomBaseY = bodyBottomY - 1;

    return (
      <>
        {topTriangles && (
          <>
            {(() => {
              const tipY = topBaseY + triHeight;
              const baseOuterX = leftXAtY(topBaseY);
              const baseInnerX = baseOuterX + 9;
              const tipX = leftXAtY(tipY);
              return (
                <polygon
                  points={`
                    ${baseOuterX},${topBaseY}
                    ${baseInnerX},${topBaseY}
                    ${tipX},${tipY}
                  `}
                  fill={secondaryColor}
                />
              );
            })()}
            {(() => {
              const tipY = topBaseY + triHeight;
              const baseOuterX = rightXAtY(topBaseY);
              const baseInnerX = baseOuterX - 9;
              const tipX = rightXAtY(tipY);
              return (
                <polygon
                  points={`
                    ${baseInnerX},${topBaseY}
                    ${baseOuterX},${topBaseY}
                    ${tipX},${tipY}
                  `}
                  fill={secondaryColor}
                />
              );
            })()}
          </>
        )}

        {bottomTriangles && (
          <>
            {(() => {
              const tipY = bottomBaseY - triHeight;
              const baseOuterX = leftXAtY(bottomBaseY);
              const baseInnerX = baseOuterX + 10;
              const tipX = leftXAtY(tipY);
              return (
                <polygon
                  points={`
                    ${baseOuterX},${bottomBaseY}
                    ${baseInnerX},${bottomBaseY}
                    ${tipX},${tipY}
                  `}
                  fill={secondaryColor}
                />
              );
            })()}
            {(() => {
              const tipY = bottomBaseY - triHeight;
              const baseOuterX = rightXAtY(bottomBaseY);
              const baseInnerX = baseOuterX - 10;
              const tipX = rightXAtY(tipY);
              return (
                <polygon
                  points={`
                    ${baseInnerX},${bottomBaseY}
                    ${baseOuterX},${bottomBaseY}
                    ${tipX},${tipY}
                  `}
                  fill={secondaryColor}
                />
              );
            })()}
          </>
        )}
      </>
    );
  };

  const renderBigSideTriangles = () => {
    if (variant !== 'sideBigTriangles') return null;

    const topY = bodyTopY;
    const bottomY = bodyBottomY;

    const leftSeamTopX = leftTopX;
    const leftSeamBottomX = leftBottomX;
    const leftInnerBottomX = leftBottomX + (crotchX - leftBottomX) * 0.5;

    const rightSeamTopX = rightTopX;
    const rightSeamBottomX = rightBottomX;
    const rightInnerBottomX = rightBottomX + (crotchX - rightBottomX) * 0.5;

    return (
      <>
        <polygon
          points={`
            ${leftSeamTopX},${topY}
            ${leftSeamBottomX},${bottomY}
            ${leftInnerBottomX},${bottomY}
          `}
          fill={secondaryColor}
        />
        <polygon
          points={`
            ${rightSeamTopX},${topY}
            ${rightInnerBottomX},${bottomY}
            ${rightSeamBottomX},${bottomY}
          `}
          fill={secondaryColor}
        />
      </>
    );
  };

  const renderSideSemiCircles = () => {
    if (variant !== 'sideSemiCircles') return null;

    const height = bodyBottomY - bodyTopY;
    const radius = height / 2 + 1;
    const centerY = (bodyTopY + bodyBottomY) / 2;

    const offsetFactor = 0.6;

    const leftCenterX = leftXAtY(centerY) - radius * offsetFactor;
    const rightCenterX = rightXAtY(centerY) + radius * offsetFactor;

    return (
      <g clipPath={`url(#${clipId})`}>
        <circle cx={leftCenterX} cy={centerY} r={radius} fill={secondaryColor} />
        <circle cx={rightCenterX} cy={centerY} r={radius} fill={secondaryColor} />
      </g>
    );
  };

  return (
    <svg
      className={svgClassName}
      viewBox="0 0 120 100"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <clipPath id={clipId}>
          <path d={bodyPath} />
        </clipPath>
      </defs>

      <path d={bodyPath} fill={primaryColor} stroke="none" />

      {renderSideStripes()}
      {renderSideTriangles()}
      {renderBigSideTriangles()}
      {renderSideSemiCircles()}

      <path d={bodyPath} fill="none" {...outlineStrokeProps} />
    </svg>
  );
};
